import { AxiosInstance, AxiosResponse } from "axios";
import { ContractNoteRequest, Transaction } from "@/types/Transactions";
import { downloadFile, getFilenameFromHeaders, getResponseFilename } from "./api";
import { Language } from "@/i18n/languages";
import { DCEvent, logErrorEvent, showGeneralError, thunk } from "@/events/eventsDomain";
import { ExportFormat } from "@/shareholderRegister/shareholders/ExportShareholdersDomain";
import { TFunction } from "@/i18n/tt";
import { logError } from "@/utils/log";
import { uniqBy } from "lodash";

export class DownloadService {
  constructor(private api: AxiosInstance) {}

  exportShareholders(
    orgNumber: string,
    language: string,
    format: string,
    date: string,
    exportFormat: ExportFormat,
  ): Promise<AxiosResponse> {
    return this.api.get(`/shareholder/export/${orgNumber}`, {
      params: {
        language,
        format,
        date,
        exportFormat,
      },
      responseType: "blob",
    });
  }

  exportShareholdersBatch(
    organizationnumbers: string[],
    language: string,
    format: string,
    date: string,
    exportFormat: ExportFormat,
  ): Promise<AxiosResponse> {
    return this.api.post(
      `/shareholder/exports`,
      {
        organizationnumbers,
        language,
        format,
        date,
        exportFormat,
      },
      {
        responseType: "blob",
      },
    );
  }

  exportShareholdersData(orgNumber: string): Promise<AxiosResponse> {
    return this.api.get(`/shareholder/export-shareholders/${orgNumber}`, {
      responseType: "blob",
    });
  }

  exportTransactions(orgNumber: string, language: Language): Promise<AxiosResponse> {
    return this.api.get(`/transactionlog/export/${orgNumber}`, {
      params: { language },
      responseType: "blob",
    });
  }

  downloadTransactionsTemplate(orgNumber: string, language: Language): Promise<AxiosResponse> {
    return this.api.get(`/transactionlog/template/${orgNumber}`, {
      params: { language },
      responseType: "blob",
    });
  }

  downloadContractNote(transactionLogId: number): Promise<void> {
    return this.api
      .get(`/contract-note-pdf/${transactionLogId}`, {
        headers: { Accept: "*/*" },
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getFilenameFromHeaders(response.headers));
      });
  }

  downloadContractNoteForCurrentUser(
    organisationNumber: string,
    request: ContractNoteRequest,
    shareholderId: number,
  ): Promise<void> {
    return this.api
      .post(
        `/shareholder-view/${organisationNumber}/download-contract-note/${shareholderId}`,
        request,
        {
          headers: { Accept: "application/pdf" },
          responseType: "blob",
        },
      )
      .then((response: AxiosResponse) => {
        downloadFile(response.data, request.fileName);
      });
  }

  downloadShareholderCertificate(
    shareholderId: string | number,
    locale: Language,
  ): Promise<AxiosResponse> {
    return this.api.get(`/shareholder-certificate/${shareholderId}?lang=${locale}`, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    });
  }

  downloadTrankMap(organisationNumber: string, threshold: number): Promise<string> {
    return this.api
      .get<string>(`/company/shareholder-map/${organisationNumber}/${threshold}`, {
        headers: { Accept: "image/svg+xml" },
      })
      .then((r) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(r.data)))}`);
  }

  downloadShareholderCertificateForCurrentUser(
    organisationNumber: string,
    shareholderId: number,
    locale: Language,
  ): Promise<void> {
    return this.api
      .get(`/shareholder-view/${organisationNumber}/certificate/${shareholderId}/${locale}`, {
        headers: { Accept: "application/pdf" },
        responseType: "blob",
      })
      .then((response: AxiosResponse) => {
        downloadFile(response.data, getResponseFilename(response));
      });
  }

  downloadNoticeToGeneralMeetingAttachment(noticeOfGeneralMeetingEmailId: string, fileId: string) {
    return downloadFileContent(
      `/shareholder-view/notice-general-meeting/${noticeOfGeneralMeetingEmailId}/attachment/${fileId}`,
      this.api,
    );
  }
}

export function downloadPdf(url: string, api: AxiosInstance) {
  return api
    .get(url, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) => {
      downloadFile(response.data, getFilenameFromHeaders(response.headers));
    });
}

export function downloadFileContent(url: string, api: AxiosInstance) {
  return api
    .post(url, null, {
      headers: { Accept: "*/*" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) =>
      downloadFile(response.data, getFilenameFromHeaders(response.headers)),
    )
    .catch(() => {
      return Promise.reject("failed to download the attachment:" + url);
    });
}

export function downloadDocument(url: string, api: AxiosInstance, filename?: string) {
  return api
    .get(url, {
      headers: { Accept: "application/pdf" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) =>
      downloadFile(
        response.data,
        filename ?? getFilenameFromHeaders(response.headers) ?? "file.pdf",
      ),
    );
}

export type DownloadResult = [
  file: Blob | { data: Blob | ArrayBuffer },
  filename: string | undefined,
];

export function download(url: string, api: AxiosInstance): Promise<DownloadResult> {
  return api
    .get(url, {
      headers: { Accept: "application/pdf, application/zip" },
      responseType: "blob",
    })
    .then((response: AxiosResponse) => [response.data, getFilenameFromHeaders(response.headers)]);
}

export const downloadEvent = ({
  url,
  onSuccess,
  onFailure,
}: {
  url: string;
  onSuccess?: () => DCEvent[];
  onFailure?: () => DCEvent[];
}): DCEvent[] => [
  {
    type: "http",
    method: "get",
    url,
    headers: { Accept: "application/pdf, application/zip" },
    responseType: "blob",
    onSuccess: (response) => [
      thunk(() => {
        downloadFile(response.data, getFilenameFromHeaders(response.headers));
      }),
      ...(onSuccess?.() ?? []),
    ],
    onFailure: (err) => [
      logErrorEvent(err, `Failed to download file: ${url}`),
      showGeneralError(),
      ...(onFailure?.() ?? []),
    ],
  },
];

export function prepareContractNoteRequests(
  transaction: Transaction,
  t: TFunction,
): ContractNoteRequest[] {
  const transactionLogId = transaction.transactionlogId;
  const shareholderSellerId: number | undefined = transaction.transactionDetails.map(
    (it) => it.shareholderSellerId,
  )[0];
  if (shareholderSellerId === undefined) {
    logError(
      `Trying to download contract notes for invalid transaction (${transactionLogId}): no seller ID`,
    );
    return [];
  }
  const transactionDetailsWithBuyers = uniqBy(
    transaction.transactionDetails.filter((it) => it.shareholderBuyerId),
    "shareholderBuyerId",
  );
  return transactionDetailsWithBuyers.map((it) => ({
    transactionLogId,
    shareholderSellerId,
    shareholderBuyerId: it.shareholderBuyerId!,
    fileName: `${t("shareholders.downloadContractNotes.fileNamePrefix")}${it.shareholderBuyerName?.replace(" ", "-")}.pdf`,
  }));
}
